import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";
import { uploadStore } from "@/lib/uploadStore";

const UploadContext = createContext(null);
const MAX_PARALLEL = 3;
const DONE_TTL_MS = 5000;
const RETRY_BASE_MS = 5000;
const RETRY_MAX_MS = 60000;
export const UPLOAD_DONE_EVENT = "inbox:upload-done";

let _seq = 0;

function describeError(e) {
  const status = e?.response?.status;
  if (!e?.response) return { msg: "Server nicht erreichbar – Verbindung unterbrochen", transient: true };
  if (status === 413) return { msg: "Datei zu gross für den Upload", transient: false };
  if (status === 401 || status === 403) return { msg: e.response?.data?.detail || "Keine Berechtigung – bitte neu anmelden", transient: false };
  if (status === 429) return { msg: "Zu viele Anfragen – gleich nochmals", transient: true };
  if (status >= 500) return { msg: `Serverfehler (${status}) – ${e.response?.data?.detail || "wird wiederholt"}`, transient: true };
  return { msg: e.response?.data?.detail || `Upload fehlgeschlagen (${status})`, transient: false };
}

export function UploadProvider({ children }) {
  const [uploads, setUploads] = useState([]);
  const filesRef = useRef({});
  const runningRef = useRef(0);
  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;
  const retryTimersRef = useRef({});

  const patch = useCallback((id, data) => {
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...data } : u)));
  }, []);

  const clearTimer = useCallback((id) => {
    clearTimeout(retryTimersRef.current[id]);
    delete retryTimersRef.current[id];
  }, []);

  const remove = useCallback((id) => {
    delete filesRef.current[id];
    clearTimer(id);
    uploadStore.remove(id);
    setUploads((prev) => prev.filter((u) => u.id !== id));
  }, [clearTimer]);

  // Transiente Fehler (Server weg, 5xx) → unbegrenzt wiederholen, Wartezeit wächst bis max. 60 s
  const scheduleAutoRetry = useCallback((job, msg) => {
    const attempt = (job.attempt || 0) + 1;
    const delay = Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** Math.min(attempt - 1, 6));
    patch(job.id, { status: "retrying", progress: 0, attempt, retryAt: Date.now() + delay, error: msg });
    retryTimersRef.current[job.id] = setTimeout(() => {
      delete retryTimersRef.current[job.id];
      patch(job.id, { status: "queued", error: null });
    }, delay);
  }, [patch]);

  const runOne = useCallback(async (job) => {
    const entry = filesRef.current[job.id];
    if (!entry) return;
    runningRef.current += 1;
    patch(job.id, { status: "uploading", progress: 0, error: null });
    const fd = new FormData();
    fd.append("files", entry.file, job.name);
    const o = entry.opts || {};
    if (o.note) fd.append("note", o.note);
    if (o.purpose) fd.append("purpose", o.purpose);
    if (o.paid_by_employee && o.employee_id) {
      fd.append("paid_by_employee", "true");
      fd.append("employee_id", o.employee_id);
    }
    try {
      const r = await apiClient.post(`/companies/${job.companyId}/inbox/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => {
          const total = ev.total || entry.file.size || 1;
          const pct = Math.min(100, Math.round((ev.loaded / total) * 100));
          patch(job.id, { progress: pct, status: pct >= 100 ? "processing" : "uploading" });
        },
      });
      const failed = (r.data?.items || []).find((x) => x.error);
      uploadStore.remove(job.id);
      if (failed) {
        patch(job.id, { status: "error", progress: 100, error: failed.error, permanent: true });
      } else {
        patch(job.id, { status: "done", progress: 100, doneAt: Date.now() });
        const payouts = (r.data?.items || []).filter((x) => x.kind === "payout_report");
        payouts.forEach((x) => {
          const ex = x.extracted || {};
          toast.success(`«${job.name}» als Auszahlungsbericht erkannt (${ex.supplier_name || "Zahlungsdienst"}, überwiesen CHF ${Number(ex.total_amount || 0).toFixed(2)})`, {
            description: "Liegt unter «Auszahlungsberichte» oben im Posteingang – nicht in der Beleg-Liste.", duration: 9000 });
        });
        window.dispatchEvent(new CustomEvent(UPLOAD_DONE_EVENT, { detail: { companyId: job.companyId, payoutIds: payouts.map((x) => x.payout_id).filter(Boolean) } }));
      }
    } catch (e) {
      const { msg, transient } = describeError(e);
      if (transient) scheduleAutoRetry(job, msg);
      else patch(job.id, { status: "error", progress: 0, error: msg, permanent: true });
    } finally {
      runningRef.current -= 1;
    }
  }, [patch, scheduleAutoRetry]);

  useEffect(() => {
    if (runningRef.current >= MAX_PARALLEL) return;
    const next = uploads.filter((u) => u.status === "queued").slice(0, MAX_PARALLEL - runningRef.current);
    next.forEach(runOne);
  }, [uploads, runOne]);

  // Wartende Wiederholungen sofort starten, sobald Netz/Tab wieder da ist
  const retryNowAll = useCallback(() => {
    uploadsRef.current.filter((u) => u.status === "retrying").forEach((u) => {
      clearTimer(u.id);
      patch(u.id, { status: "queued", error: null });
    });
  }, [clearTimer, patch]);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") retryNowAll(); };
    window.addEventListener("online", retryNowAll);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", retryNowAll);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [retryNowAll]);

  // Nach Neuladen: noch nicht abgeschlossene Uploads aus dem Browser-Speicher wieder in die Warteschlange
  useEffect(() => {
    let cancelled = false;
    uploadStore.all().then((rows) => {
      if (cancelled || !rows.length) return;
      const restored = rows.filter((r) => !filesRef.current[r.id] && r.file).map((r) => {
        filesRef.current[r.id] = { file: r.file, opts: r.opts };
        return { id: r.id, companyId: r.companyId, name: r.name, size: r.size, status: "queued", progress: 0, error: null, restored: true };
      });
      if (!restored.length) return;
      setUploads((prev) => [...prev, ...restored.filter((r) => !prev.some((p) => p.id === r.id))]);
      toast.info(`${restored.length} unterbrochene(r) Upload(s) werden automatisch fortgesetzt`);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const done = uploads.filter((u) => u.status === "done" && u.doneAt);
    if (!done.length) return;
    const timers = done.map((u) => setTimeout(() => remove(u.id), Math.max(0, DONE_TTL_MS - (Date.now() - u.doneAt))));
    return () => timers.forEach(clearTimeout);
  }, [uploads, remove]);

  const active = useMemo(() => uploads.filter((u) => ["queued", "uploading", "processing", "retrying"].includes(u.status)), [uploads]);
  const troubled = useMemo(() => uploads.filter((u) => ["error", "retrying"].includes(u.status)), [uploads]);

  useEffect(() => {
    if (!active.length) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [active.length]);

  const enqueue = useCallback((companyId, fileList, opts) => {
    const files = Array.from(fileList || []);
    if (!companyId || !files.length) return 0;
    const jobs = files.map((file) => {
      const id = `up-${Date.now()}-${++_seq}`;
      filesRef.current[id] = { file, opts };
      uploadStore.put({ id, companyId, name: file.name, size: file.size, file, opts: opts || {}, createdAt: Date.now() });
      return { id, companyId, name: file.name, size: file.size, status: "queued", progress: 0, error: null };
    });
    setUploads((prev) => [...prev, ...jobs]);
    toast.info(`${files.length} Datei(en) in die Warteschlange gelegt — Upload läuft im Hintergrund weiter`);
    return files.length;
  }, []);

  const retry = useCallback((id) => {
    clearTimer(id);
    patch(id, { status: "queued", progress: 0, error: null, attempt: 0, permanent: false });
  }, [clearTimer, patch]);

  const retryAllFailed = useCallback(() => {
    uploadsRef.current.filter((u) => ["error", "retrying"].includes(u.status) && filesRef.current[u.id]).forEach((u) => retry(u.id));
  }, [retry]);

  const dismiss = useCallback((id) => {
    const u = uploadsRef.current.find((x) => x.id === id);
    if (u && ["uploading", "processing"].includes(u.status)) return;
    remove(id);
  }, [remove]);

  const clearFinished = useCallback(() => {
    setUploads((prev) => {
      prev.filter((u) => ["done", "error"].includes(u.status)).forEach((u) => { delete filesRef.current[u.id]; uploadStore.remove(u.id); });
      return prev.filter((u) => !["done", "error"].includes(u.status));
    });
  }, []);

  const value = useMemo(() => ({ uploads, active, troubled, enqueue, retry, retryAllFailed, dismiss, clearFinished }),
    [uploads, active, troubled, enqueue, retry, retryAllFailed, dismiss, clearFinished]);
  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
}

export function useUploads() {
  const ctx = useContext(UploadContext);
  if (!ctx) throw new Error("useUploads must be used within UploadProvider");
  return ctx;
}
